import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import { Box, Stack, Typography, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserProvider } from "./context/UserContext";
import { Layout } from "./components/Layout";
import { PageTitleProvider } from "./context/PageTitleContext";
import { NavigationHistoryProvider } from "./context/NavigationHistoryContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { HomePage } from "./pages/HomePage";
import { UsersPage } from "./pages/UsersPage";
import { GroupsPage } from "./pages/GroupsPage";
import { PermissionsPage } from "./pages/PermissionsPage";
import { OdooRemoteSyncPage } from "./pages/OdooRemoteSyncPage";
import { OdooProductionSyncPage } from "./pages/OdooProductionSyncPage";
import { ForecastPage } from "./pages/ForecastPage";
import { DistribucionDashboardPage } from "./pages/DistribucionDashboardPage";
import { InventarioAperturaPage } from "./pages/InventarioAperturaPage";
import { DistribucionTiendaPage } from "./pages/DistribucionTiendaPage";
import { DistribucionPreviewPage } from "./pages/DistribucionPreviewPage";
import { TransferOrdersPage } from "./pages/TransferOrdersPage";
import { BacklogDistribucionPage } from "./pages/BacklogDistribucionPage";
import { DenegacionesPage } from "./pages/DenegacionesPage";
import { DiferenciasTransferenciasPage } from "./pages/DiferenciasTransferenciasPage";
import { SubcompraAlertPage } from "./pages/SubcompraAlertPage";
import { MetricasExitoPage } from "./pages/MetricasExitoPage";
import { ConfiguracionDistribucionPage } from "./pages/ConfiguracionDistribucionPage";
import { ConfiguracionForecastPage } from "./pages/configuracion-forecast/ConfiguracionForecastPage";
import VentasPage from "./pages/VentasPage";
import VentasB2BPage from "./pages/VentasB2BPage";
import { EcommerceImportacionesPage } from "./pages/EcommerceImportacionesPage";
import { EcommerceResultadosPage } from "./pages/EcommerceResultadosPage";
import { EcommerceResumenMensualPage } from "./pages/EcommerceResumenMensualPage";
import MargenesPage from "./pages/MargenesPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import CategoryReviewPage from "./pages/CategoryReviewPage";
import MarcasPage from "./pages/MarcasPage";
import "./translations"; // Import Spanish translations

// Configuración de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutos
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Componente principal ultra-minimalista y elegante
 * Pantalla de login sin tarjetas, fondo blanco, tipografía ligera
 * Componentes nativos sin restricciones de ancho
 */
export function App(): React.ReactElement {
  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <Authenticator
          components={{
            Header() {
              return (
                <Stack
                  alignItems="center"
                  spacing={2}
                  sx={{
                    mb: 4,
                    textAlign: "center",
                    padding: "2rem 0",
                  }}
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      color: "#000000",
                      fontWeight: 300,
                      letterSpacing: "-0.01em",
                      fontSize: { xs: "2.5rem", md: "3rem" },
                    }}
                  >
                    VillApps
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#888888",
                      fontWeight: 300,
                      letterSpacing: "0.02em",
                      fontSize: { xs: "1.25rem", md: "1.5rem" },
                    }}
                  >
                    Mas X Pieza
                  </Typography>
                </Stack>
              );
            },
          }}
        >
          {({ signOut, user }) => (
            <QueryClientProvider client={queryClient}>
              <UserProvider cognitoUser={user}>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <NavigationHistoryProvider>
                    <PageTitleProvider>
                      <Layout signOut={signOut}>
                        <Routes>
                          {/* Ruta pública - sin protección */}
                          <Route path="/" element={<HomePage />} />

                          {/* Ruta de acceso no autorizado */}
                          <Route
                            path="/unauthorized"
                            element={<UnauthorizedPage />}
                          />

                          {/* Ventas - Protegidas */}
                          <Route
                            path="/ventas"
                            element={
                              <ProtectedRoute
                                ruta="/ventas"
                                menu="ventas"
                                funcion="ver"
                              >
                                <VentasPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/margenes"
                            element={
                              <ProtectedRoute
                                ruta="/ventas"
                                menu="margenes"
                                funcion="ver"
                              >
                                <MargenesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ventas-b2b"
                            element={
                              <ProtectedRoute
                                ruta="/ventas-b2b"
                                menu="clientes"
                                funcion="ver"
                              >
                                <VentasB2BPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Ecommerce ML/Amazon/Walmart (F.047/F.048) - Protegidas */}
                          <Route
                            path="/mercado-libre"
                            element={
                              <ProtectedRoute
                                ruta="/mercado-libre"
                                menu="importacion"
                                funcion="ver"
                              >
                                <EcommerceImportacionesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/mercado-libre/resultados"
                            element={
                              <ProtectedRoute
                                ruta="/mercado-libre"
                                menu="importacion"
                                funcion="ver"
                              >
                                <EcommerceResultadosPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/mercado-libre/resumen"
                            element={
                              <ProtectedRoute
                                ruta="/mercado-libre"
                                menu="importacion"
                                funcion="ver"
                              >
                                <EcommerceResumenMensualPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Usuarios - Protegidas */}
                          <Route
                            path="/usuarios"
                            element={
                              <ProtectedRoute
                                ruta="/usuarios"
                                menu="gestion-usuarios"
                                funcion="ver"
                              >
                                <UsersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/grupos"
                            element={
                              <ProtectedRoute
                                ruta="/usuarios"
                                menu="gestion-grupos"
                                funcion="ver"
                              >
                                <GroupsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/permisos"
                            element={
                              <ProtectedRoute
                                ruta="/usuarios"
                                menu="gestion-permisos"
                                funcion="ver"
                              >
                                <PermissionsPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Compras - Protegidas */}
                          <Route
                            path="/compras/forecast"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="forecast"
                                funcion="ver"
                              >
                                <ForecastPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/categorias/revision"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <CategoryReviewPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <DistribucionDashboardPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/transferencias"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="editar"
                              >
                                <DistribucionTiendaPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/apertura"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="editar"
                              >
                                <InventarioAperturaPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/preview/:id"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="editar"
                              >
                                <DistribucionPreviewPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/historial"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <TransferOrdersPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/backlog"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <BacklogDistribucionPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/denegaciones"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <DenegacionesPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/diferencias"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="editar"
                              >
                                <DiferenciasTransferenciasPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/alertas-subcompra"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <SubcompraAlertPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/metricas"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="ver"
                              >
                                <MetricasExitoPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/distribucion/configuracion"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="distribucion"
                                funcion="editar"
                              >
                                <ConfiguracionDistribucionPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/forecast/configuracion"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="forecast"
                                funcion="editar"
                              >
                                <ConfiguracionForecastPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compras/marcas"
                            element={
                              <ProtectedRoute
                                ruta="/compras"
                                menu="forecast"
                                funcion="ver"
                              >
                                <MarcasPage />
                              </ProtectedRoute>
                            }
                          />

                          {/* Odoo - Protegidas */}
                          <Route
                            path="/odoo/remote-sync"
                            element={
                              <ProtectedRoute
                                ruta="/odoo-erp"
                                menu="remote-sync"
                                funcion="ver"
                              >
                                <OdooRemoteSyncPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/odoo/production-sync"
                            element={
                              <ProtectedRoute
                                ruta="/odoo-erp"
                                menu="migration-produccion"
                                funcion="ver"
                              >
                                <OdooProductionSyncPage />
                              </ProtectedRoute>
                            }
                          />
                        </Routes>
                      </Layout>
                    </PageTitleProvider>
                  </NavigationHistoryProvider>
                </Router>
              </UserProvider>
            </QueryClientProvider>
          )}
        </Authenticator>
      </Box>
    </>
  );
}
